import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import menuData from "../../data/SidebarMenu.json";
import {
  FaHome,
  FaUsers,
  FaBriefcase,
  FaUser,
  FaTicketAlt,
  FaLayerGroup,
  FaAppStore,
  FaBook,
  FaCog,
  FaUserCircle,
  FaListUl,
  FaBus,
  FaBoxOpen,
} from "react-icons/fa";
import { PiNoteFill } from "react-icons/pi";
import { AiFillBank } from "react-icons/ai";
import { HiDocumentCurrencyRupee } from "react-icons/hi2";
import { TbFileInvoice } from "react-icons/tb";
import { MdApps, MdPayments, MdDevices } from "react-icons/md";
import { TbReportMoney, TbReportSearch } from "react-icons/tb";
import { FaDatabase } from "react-icons/fa";

const icons = {
  home: <FaHome size={16} />,
  users: <FaUsers size={16} />,
  briefcase: <FaBriefcase size={16} />,
  user: <FaUser size={16} />,
  ticket: <FaTicketAlt size={16} />,
  layers: <FaLayerGroup size={16} />,
  "app-window": <FaAppStore size={16} />,
  book: <FaBook size={16} />,
  settings: <FaCog size={16} />,
  taxes: <HiDocumentCurrencyRupee size={16} />,
  invoices: <TbFileInvoice size={16} />,
  payments: <MdPayments size={16} />,
  trips: <FaBus size={16} />,
  banks: <AiFillBank size={16} />,
  box: <FaBoxOpen size={16} />,
  "user-circle": <FaUserCircle size={16} />,
  "release-note": <PiNoteFill size={16} />,
  transactions: <FaListUl size={16} />,
  apps: <MdApps size={16} />,
  devices: <MdDevices size={16} />,
  subscription: <TbReportMoney size={16} />,
  reports: <TbReportSearch size={16} />,
  data: <FaDatabase size={16} />,
};

const createActions = (role) => {
  const actions = [];

  if (!Array.isArray(menuData)) return actions;

  menuData.forEach((menu) => {
    // Skip parent items without children
    if (!menu.children || menu.children.length === 0) return;

    const parentIcon = menu.icon;

    menu.children.forEach((child) => {
      // Role-based access check (type)
      if (!child.allow_roles || !child.allow_roles.includes(role)) return;

      // Only show "Create" type quick actions
      if (!child.title.toLowerCase().includes("create")) return;

      actions.push({
        key: child.url,
        title: child.title,
        url: child.url,
        icon: parentIcon,
        allow_roles: child.allow_roles,
      });
    });
  });

  return actions;
};

export default function QuickAction() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.type || "intern";

  const actions = createActions(role);

  // Close dropdown when clicking outside (matches ProfileWidgets behavior)
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (url) => {
    setOpen(false);
    navigate(url);
  };

  return (
    <div className="relative inline-block select-none" ref={menuRef}>
      {/* BUTTON */}
      <button
        onClick={() => setOpen((p) => !p)}
        className="z-10 w-10 h-10 rounded-2xl bg-primary-500 hover:bg-primary-600 text-white shadow-md shadow-primary-500/30 transition-all flex items-center justify-center cursor-pointer"
        title="Quick Actions"
        aria-label="Quick Actions"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <MdApps size={16} className="w-5 h-5" />
      </button>

      {/* DROPDOWN MENU */}
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-[24px] border border-surface-border dark:border-surface-darkBorder bg-white dark:bg-surface-darkCard shadow-2xl z-[999999] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <div className="px-4 py-3 border-b border-surface-border dark:border-surface-darkBorder bg-surface-light dark:bg-surface-darkMuted">
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-300 uppercase tracking-wider">
              Quick Actions
            </span>
          </div>

          <div className="max-h-[400px] overflow-y-auto scroll-bar">
            {actions.map((action) => (
              <button
                key={action.key}
                onClick={() => handleSelect(action.url)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all hover:bg-primary-50 dark:hover:bg-surface-darkMuted border-b border-surface-border/50 dark:border-surface-darkBorder/50 last:border-0"
              >
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                    action.key.includes("/invoice")
                      ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                  }`}
                >
                  {icons[action.icon]}
                </div>
                <span className="text-sm font-medium text-gray-800 dark:text-white">
                  {action.title}
                </span>
                <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-300">
                  {action.url.replace("/", "")}
                </span>
              </button>
            ))}
            {actions.length === 0 && (
              <div className="px-4 py-8 text-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  No actions available
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
